import { Box, CircularProgress, Typography } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useAuth } from "./routing/AuthContext";
import { ROUTES } from "./routing/paths";
import { useCreatePropertyWizard } from "./useCreatePropertyWizard";
import WizardShell from "./layout/WizardShell";
import {
  CreatePropertyErrorDialog,
  CreatePropertySuccessDialog,
  DeleteAccessPointDialog,
  DeleteUnitDialog,
  DuplicateEmailDialog,
  IncompleteAccessGroupsDialog,
} from "./components/ResultDialogs";
import PropertyStep from "./steps/PropertyStep";
import AccessPointsStep from "./steps/AccessPointsStep";
import AccessGroupsStep from "./steps/AccessGroupsStep";
import UnitsStep from "./steps/UnitsStep";
import UsersStep from "./steps/UsersStep";
import { PROPERTY_TYPES } from "./types";
import { UserPropertyOption } from "./auth";

interface PropertySetupWizardProps {
  mode: "new" | "edit";
  authSession?: { user: { id?: number } } | null;
  editProperty?: UserPropertyOption | null;
  documentId?: string;
}

export default function PropertySetupWizard({
  mode,
  authSession = null,
  editProperty = null,
  documentId,
}: PropertySetupWizardProps) {
  const navigate = useNavigate();
  const { refreshProperties } = useAuth();
  const isEditMode = mode === "edit";
  const wizard = useCreatePropertyWizard(authSession?.user.id, {
    editDocumentId: isEditMode ? documentId : undefined,
  });

  const handleSuccessClose = async () => {
    wizard.closeSuccessModal();
    wizard.resetWizard();
    try {
      await refreshProperties();
    } catch (error) {
      console.error("Error refreshing user properties:", error);
    }
    navigate(ROUTES.menu, { replace: true });
  };

  const dialogs = (
    <>
      <DuplicateEmailDialog
        open={wizard.duplicateEmailModalOpen}
        email={wizard.duplicateEmail}
        onClose={wizard.closeDuplicateEmailModal}
      />
      <CreatePropertyErrorDialog
        open={wizard.createErrorModalOpen}
        title={wizard.createErrorTitle}
        message={wizard.createErrorMessage}
        onClose={wizard.closeCreateErrorModal}
      />
      <IncompleteAccessGroupsDialog
        open={wizard.incompleteGroupsModalOpen}
        incompleteCount={wizard.incompleteAccessGroupsCount}
        onCancel={wizard.closeIncompleteGroupsModal}
        onContinue={wizard.continueDespiteIncompleteGroups}
      />
      <DeleteAccessPointDialog
        open={wizard.deleteAccessPointModalOpen}
        pointName={wizard.pendingDeleteAccessPointName}
        groupsToUpdate={wizard.pendingDeleteGroupsToUpdate}
        groupsToDelete={wizard.pendingDeleteGroupsToDelete}
        onCancel={wizard.closeDeleteAccessPointModal}
        onConfirm={wizard.confirmRemoveAccessPoint}
      />
      <DeleteUnitDialog
        open={wizard.deleteUnitModalOpen}
        unitName={wizard.pendingDeleteUnitName}
        affectedUsers={wizard.pendingDeleteUnitUsers}
        availableUnits={wizard.pendingDeleteUnitReassignOptions}
        onCancel={wizard.closeDeleteUnitModal}
        onConfirm={wizard.confirmRemoveUnit}
      />
      <CreatePropertySuccessDialog
        open={wizard.successModalOpen}
        summary={wizard.successSummary}
        onClose={handleSuccessClose}
      />
    </>
  );

  if (wizard.isLoading) {
    return (
      <>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            minHeight: "100vh",
            gap: 2,
          }}
        >
          <CircularProgress />
          <Typography color="text.secondary">{wizard.loadingMessage}</Typography>
        </Box>
        {dialogs}
      </>
    );
  }

  const typeLabel =
    PROPERTY_TYPES.find((t) => t.value === wizard.prop.type)?.label ?? "";

  const footerNote =
    wizard.step === 1
      ? `${typeLabel ? typeLabel + " · " : ""}${wizard.prop.name || "Propiedad sin nombre"}`
      : wizard.step === 2
        ? `${wizard.accessPoints.length} puntos · ${wizard.accessPoints.reduce((a, p) => a + p.gates.length, 0)} puertas`
        : wizard.step === 3
          ? `${wizard.groups.length} grupos configurados`
          : wizard.step === 4
            ? `${wizard.units.length} unidades · Administración incluida`
            : `${wizard.users.length} usuarios · ${wizard.users.filter((u) => u.role === "Administración").length} de Administración`;

  return (
    <>
      <WizardShell
        step={wizard.step}
        onGoToStep={wizard.setStep}
        completedSteps={wizard.completedSteps}
        footerNote={footerNote}
        nextDisabled={wizard.nextDisabled}
        nextLabel={wizard.isEditMode ? "Guardar cambios" : undefined}
        onBack={wizard.back}
        onNext={wizard.next}
        editMode={wizard.isEditMode}
        editPropertyName={
          wizard.prop.name || editProperty?.name || "Propiedad sin nombre"
        }
        editDocumentId={wizard.editDocumentId || documentId}
      >
        {wizard.step === 1 && (
          <PropertyStep
            companies={wizard.companies}
            companiesLoading={wizard.companiesLoading}
            selectedCompany={wizard.selectedCompany}
            onSelectCompany={wizard.setSelectedCompany}
            newCompanyName={wizard.newCompanyName}
            onNewCompanyNameChange={wizard.setNewCompanyName}
            prop={wizard.prop}
            onUpdateProp={wizard.updateProp}
            onToggleSetting={wizard.setPropSetting}
            timeZones={wizard.timeZones}
            timeZonesLoading={wizard.timeZonesLoading}
          />
        )}
        {wizard.step === 2 && (
          <AccessPointsStep
            accessPoints={wizard.accessPoints}
            selectedPointId={wizard.selectedPointId}
            onSelectPoint={wizard.setSelectedPointId}
            onUpdatePoint={wizard.updateAccessPoint}
            onAddPoint={wizard.addAccessPoint}
            onRemovePoint={wizard.removeAccessPoint}
            layoutImage={wizard.prop.layoutImage}
            layoutPins={wizard.prop.layoutPins ?? []}
            onLayoutImageChange={(layoutImage) =>
              wizard.updateProp({ layoutImage })
            }
            onLayoutPinsChange={(layoutPins) =>
              wizard.updateProp({ layoutPins })
            }
          />
        )}
        {wizard.step === 3 && (
          <AccessGroupsStep
            groups={wizard.groups}
            selectedGroupId={wizard.selectedGroupId}
            onSelectGroup={wizard.setSelectedGroupId}
            onAddGroup={wizard.addGroup}
            onUpdateGroup={wizard.updateGroup}
            onRemoveGroup={wizard.removeGroup}
            accessPoints={wizard.accessPoints}
            hasScheduleRestrictions={
              wizard.prop.settings.has_schedule_restrictions
            }
            onToggleScheduleRestrictions={(enabled) =>
              wizard.setPropSetting("has_schedule_restrictions", enabled)
            }
            hasRequirementsPermissions={
              wizard.prop.settings.has_requirements_permissions
            }
          />
        )}
        {wizard.step === 4 && (
          <UnitsStep
            units={wizard.units}
            users={wizard.users}
            newUnitName={wizard.newUnitName}
            onNewUnitNameChange={wizard.setNewUnitName}
            onAddUnit={wizard.addUnit}
            onRenameUnit={wizard.renameUnit}
            onRemoveUnit={wizard.removeUnit}
            onAddUserToUnit={wizard.addUserToUnit}
          />
        )}
        {wizard.step === 5 && (
          <UsersStep
            users={wizard.users}
            onRemoveUser={wizard.removeUser}
            newUser={wizard.newUser}
            onUpdateNewUser={wizard.updateNewUser}
            onAddUser={wizard.addUser}
            units={wizard.units}
            accessPoints={wizard.accessPoints}
            hasAdminUser={wizard.hasAdminUser}
          />
        )}
      </WizardShell>

      {dialogs}
    </>
  );
}
